import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, Badge, Card, CardContent, CardHeader, CardTitle, SkeletonCard } from "@/components/ui";
import { isApiError } from "@/api/client";
import { EVENT_STATUS_BADGE_VARIANT, EVENT_STATUS_LABELS } from "@/api/types/event";
import { parseIsoDate } from "@/features/events/eventDate";
import { portalKeys, usePortalMe } from "./usePortal";
import { PortalInvoicesCard } from "./PortalInvoicesCard";
import { PortalContractCard } from "./PortalContractCard";
import type { PortalEvent } from "./types";

const BUSINESS_NAME = "The Signature Pour";
const INVALID_LINK_MESSAGE =
  "This link is invalid or has expired — please ask The Signature Pour for a new one.";

/**
 * PUBLIC client portal (U4b) — reached via a magic-link URL
 * (`/portal?token=<jwt>`), never behind the staff auth guard (see App.tsx:
 * this route sits outside <RequireAuth>). Consumes the U4a backend
 * (app/api/portal.py) through the dedicated `portalClient` in usePortal.ts,
 * never the staff `apiClient` — a bad/expired token here is a normal
 * customer-facing outcome, not a reason to redirect anyone to /login.
 *
 * No token in the URL at all is treated as an immediate invalid-link state
 * with NO API call (see `enabled: !!token` in usePortalMe) — there is
 * nothing valid to ask the backend for. A token that IS present but
 * rejected (401, "Invalid or expired portal link" per
 * app/api/deps.py::verify_portal_token) drives the identical UI via the
 * query's error state. Any other failure (network/5xx) falls back to the
 * shared ApiError card instead, since that's a transient server problem,
 * not proof the link itself is bad.
 *
 * `?paid=1`/`?paid=0` (appended by the Stripe Checkout success/cancel
 * redirect built in app/api/portal.py::portal_invoice_checkout) is NEVER
 * trusted to mean the invoice is actually paid — it's a client-supplied
 * query param, trivially forgeable by anyone who edits the URL. It is used
 * ONLY as a signal to force a fresh GET /me (in case a warm cache would
 * otherwise serve a stale pre-payment snapshot for the ~30s staleTime
 * window) and to show a transient "confirming your payment…" banner while
 * that refetch is in flight. The paid badge / "Pay now" button visibility
 * always comes from the refetched `data.invoices[].status` /
 * `amount_due_cents` — server truth — never from the query param itself.
 */
export function PortalPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("token");
  const paidParam = searchParams.get("paid");
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, error, refetch } = usePortalMe(token);

  const isInvalidToken = isApiError(error) && error.response?.status === 401;

  const [confirmingPayment, setConfirmingPayment] = useState(paidParam === "1");

  // `?paid=1` is only ever a hint to re-check the server, never the source
  // of truth itself — force a fresh /me fetch (bypassing any warm cache)
  // and strip the param so a refresh/bookmark of this exact URL can't
  // replay the "confirming" banner. Runs once per token/paid combination.
  useEffect(() => {
    if (paidParam !== "1" || !token) return;
    void queryClient.invalidateQueries({ queryKey: portalKeys.me(token) });
    const next = new URLSearchParams(searchParams);
    next.delete("paid");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, paidParam]);

  // Clear the transient banner once the forced refetch above has settled
  // (success or failure — either way we're no longer "confirming").
  useEffect(() => {
    if (confirmingPayment && !isFetching) {
      setConfirmingPayment(false);
    }
  }, [confirmingPayment, isFetching]);

  return (
    <div className="min-h-screen bg-bg-body px-4 py-8 sm:py-12">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
        <header className="text-center">
          <p className="font-display text-sm font-semibold uppercase tracking-wide text-copper">
            {BUSINESS_NAME}
          </p>
          <h1 className="mt-1 font-display text-2xl font-bold text-text-primary sm:text-3xl">
            Your event portal
          </h1>
        </header>

        {!token || isInvalidToken ? (
          <PortalInvalidLink />
        ) : isLoading ? (
          <PortalSkeleton />
        ) : error || !data ? (
          <ApiError error={error} onRetry={() => void refetch()} title="Unable to load your portal" />
        ) : (
          <>
            {confirmingPayment && <PortalConfirmingPaymentBanner />}
            <PortalEventCard event={data.event} />
            <PortalInvoicesCard invoices={data.invoices} token={token} />
            {data.contract && <PortalContractCard contract={data.contract} token={token} />}
          </>
        )}
      </div>
    </div>
  );
}

function PortalConfirmingPaymentBanner() {
  return (
    <div
      role="status"
      className="rounded-md border border-copper/40 bg-copper/10 px-4 py-3 text-center text-sm font-medium text-copper"
    >
      Confirming your payment…
    </div>
  );
}

function PortalInvalidLink() {
  return (
    <Card className="text-center">
      <CardContent className="flex flex-col items-center gap-3 py-8">
        <span className="text-4xl" role="img" aria-label="Locked link">
          🔒
        </span>
        <p className="text-lg font-semibold text-text-primary">Link invalid or expired</p>
        <p className="max-w-sm text-sm text-text-secondary">{INVALID_LINK_MESSAGE}</p>
      </CardContent>
    </Card>
  );
}

function PortalSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <SkeletonCard />
      <SkeletonCard />
    </div>
  );
}

function formatEventDate(iso: string): string {
  return parseIsoDate(iso).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function PortalEventCard({ event }: { event: PortalEvent }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle>Your event</CardTitle>
          <p className="mt-1 text-sm text-text-secondary">{formatEventDate(event.event_date)}</p>
        </div>
        <Badge variant={EVENT_STATUS_BADGE_VARIANT[event.status]}>
          {EVENT_STATUS_LABELS[event.status]}
        </Badge>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-text-secondary">
          Package: <span className="font-medium text-text-primary">{event.package_name ?? "—"}</span>
        </p>
      </CardContent>
    </Card>
  );
}
